import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";

import CrudService from "services/cruds-service";
import AuthService from "services/auth-service";

import { useIdleTimer } from "react-idle-timer";
import { Snackbar, Alert } from "@mui/material";

const IDLE_TIMEOUT = 30 * 60 * 1000;
const SETTINGS_STORAGE_KEY = "filenders-dashboard-settings";

const MaterialUI = createContext();
const SnackbarContext = createContext();

export const AuthContext = createContext({
  isAuthenticated: false,
  login: () => {},
  register: () => {},
  logout: () => {},
  getRole: () => {},
});

const AuthContextProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: () => {
      logout();
    },
    debounce: 500,
  });

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    const refresh = localStorage.getItem("refresh_token");

    if (token && !refresh) {
      logout();
      return false;
    }

    if (token) {
      try {
        const res = await AuthService.getProfile();

        if (res?.data?.id) {
          setIsAuthenticated(true);
          setUser({
            id: res.data.id,
            name: res.data.attributes.name,
            email: res.data.attributes.email,
            profile_image: res.data.attributes.profile_image,
          });

          setIsLoading(false);
          return true;
        }

        setIsAuthenticated(false);
        localStorage.removeItem("token");
      } catch (err) {
        setIsAuthenticated(false);
        localStorage.removeItem("token");
      }
    }

    setIsLoading(false);
    return false;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (newToken, refreshToken) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("refresh_token", refreshToken);
    setIsAuthenticated(true);

    const success = await checkAuth();

    if (success) {
      navigate("/dashboard/analytics");
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
    } catch (e) {
      console.warn("Backend logout başarısız:", e);
    }

    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    setIsAuthenticated(false);
    navigate("/auth/login");
  };

  const getRole = async () => {
    const id = user?.id;

    if (!id || id === "undefined") {
      console.warn("getRole() çağrıldı ama user.id undefined:", id);
      return null;
    }

    try {
      const res = await CrudService.getUser(id);
      const roleId = res.data.relationships.roles.data[0].id;

      if (roleId === "1") return "admin";
      if (roleId === "2") return "creator";
      if (roleId === 3) return "member";

      return res.included[0].attributes.name;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  return (
      <AuthContext.Provider
          value={{
            isAuthenticated,
            isLoading,
            setIsAuthenticated,
            login,
            logout,
            getRole,
            user,
            setUser,
          }}
      >
        {children}
      </AuthContext.Provider>
  );
};

MaterialUI.displayName = "MaterialUIContext";

const defaultControllerState = {
  miniSidenav: false,
  transparentSidenav: false,
  whiteSidenav: false,
  sidenavColor: "info",
  transparentNavbar: true,
  fixedNavbar: true,
  openConfigurator: false,
  direction: "ltr",
  layout: "dashboard",
  darkMode: false,
};

const getInitialControllerState = () => {
  try {
    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);

    if (!savedSettings) {
      return defaultControllerState;
    }

    return {
      ...defaultControllerState,
      ...JSON.parse(savedSettings),
      openConfigurator: false,
    };
  } catch (error) {
    console.error("Dashboard settings load error:", error);
    localStorage.removeItem(SETTINGS_STORAGE_KEY);
    return defaultControllerState;
  }
};

function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV":
      return { ...state, miniSidenav: action.value };

    case "TRANSPARENT_SIDENAV":
      return { ...state, transparentSidenav: action.value };

    case "WHITE_SIDENAV":
      return { ...state, whiteSidenav: action.value };

    case "SIDENAV_COLOR":
      return { ...state, sidenavColor: action.value };

    case "TRANSPARENT_NAVBAR":
      return { ...state, transparentNavbar: action.value };

    case "FIXED_NAVBAR":
      return { ...state, fixedNavbar: action.value };

    case "OPEN_CONFIGURATOR":
      return { ...state, openConfigurator: action.value };

    case "DIRECTION":
      return { ...state, direction: action.value };

    case "LAYOUT":
      return { ...state, layout: action.value };

    case "DARKMODE":
      return { ...state, darkMode: action.value };

    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
}

function MaterialUIControllerProvider({ children }) {
  const [controller, dispatch] = useReducer(
      reducer,
      undefined,
      getInitialControllerState
  );

  useEffect(() => {
    const settingsToSave = {
      miniSidenav: controller.miniSidenav,
      transparentSidenav: controller.transparentSidenav,
      whiteSidenav: controller.whiteSidenav,
      sidenavColor: controller.sidenavColor,
      transparentNavbar: controller.transparentNavbar,
      fixedNavbar: controller.fixedNavbar,
      direction: controller.direction,
      layout: controller.layout,
      darkMode: controller.darkMode,
    };

    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settingsToSave));
  }, [controller]);

  const value = useMemo(() => [controller, dispatch], [controller]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
        "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

AuthContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const SnackbarProvider = ({ children }) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
      <SnackbarContext.Provider value={{ showSnackbar }}>
        {children}

        <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleClose}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
              onClose={handleClose}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </SnackbarContext.Provider>
  );
};

SnackbarProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

const setMiniSidenav = (dispatch, value) =>
    dispatch({ type: "MINI_SIDENAV", value });

const setTransparentSidenav = (dispatch, value) =>
    dispatch({ type: "TRANSPARENT_SIDENAV", value });

const setWhiteSidenav = (dispatch, value) =>
    dispatch({ type: "WHITE_SIDENAV", value });

const setSidenavColor = (dispatch, value) =>
    dispatch({ type: "SIDENAV_COLOR", value });

const setTransparentNavbar = (dispatch, value) =>
    dispatch({ type: "TRANSPARENT_NAVBAR", value });

const setFixedNavbar = (dispatch, value) =>
    dispatch({ type: "FIXED_NAVBAR", value });

const setOpenConfigurator = (dispatch, value) =>
    dispatch({ type: "OPEN_CONFIGURATOR", value });

const setDirection = (dispatch, value) =>
    dispatch({ type: "DIRECTION", value });

const setLayout = (dispatch, value) =>
    dispatch({ type: "LAYOUT", value });

const setDarkMode = (dispatch, value) =>
    dispatch({ type: "DARKMODE", value });

export const useSnackbar = () => useContext(SnackbarContext);

export {
  AuthContextProvider,
  MaterialUIControllerProvider,
  SnackbarProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
};